// --------------------------------------------------------------
// Created On: 2025-09-30
// Author: Zachary Thomas
//
// Last Modified: 2025-11-05
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React from "react";
import Modal from "../Modal/Modal";
import ModalHeader from "../ModalHeader/ModalHeader";
import ModalBody from "../ModalBody/ModalBody";
import ModalFooter from "../ModalFooter/ModalFooter";
import PasswordInput from "../PasswordInput/PasswordInput";
import Error from "src/components/Error/Error";
import styles from "./PasswordConfirmModal.module.scss";

// Modal for confirming a critical action that needs a password to proceed.
export default function PasswordConfirmModal(props: Props): Component {
  return (
    <div data-test="password-confirm-modal">
      <Modal
        show={props.showModal}
        onHide={() => props.onCancel()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--super-modal-z-index)" }}
        animation
        centered
      >
        <ModalHeader>
          <h5>{props.title}</h5>
        </ModalHeader>
        <ModalBody>
          <p>{props.content}</p>
          <br />
          <p>Enter your password in the box below to confirm.</p>
          <PasswordInput
            password={props.password}
            onChangePassword={(password) => props.onChangePassword(password)}
            disabled={props.disabled}
          />

          {props.errorMessage !== undefined && props.errorMessage.length > 0 && (
            <div className="mt-3">
              <Error message={props.errorMessage} />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <button
            data-test="password-confirm-button"
            type="button"
            className="btn btn-primary"
            data-dismiss="modal"
            onClick={() => props.onConfirm()}
            disabled={props.disabled || props.password.length === 0}
          >
            {props.confirmText}
          </button>

          <button
            data-test="cancel-password-modal-button"
            type="button"
            className="btn btn-secondary"
            onClick={() => props.onCancel()}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface Props {
  showModal: boolean;
  title: string;
  content: string;
  password: string;
  confirmText: string;
  errorMessage?: string;
  disabled?: boolean;
  onChangePassword: (password: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}
